import { tokens } from '@cellula/react-theme-patient';
import { clamp } from '@visiba/std';
import { motion } from 'framer-motion';
import { ReactNode, UIEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { NewMessageFloatingButton } from './_chat_new_message_floating_button.component';
import { NEW_MESSAGES_BANNER_ID } from './_constants';

interface Props {
	children: ReactNode | ReactNode[];
}

const elements = {
	container: styled.div`
		position: absolute;
		width: 100%;
		height: 100%;
	`,
	scroller: styled.div`
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		max-height: 100%;
		overflow-x: hidden;
		overflow-y: auto;
		overscroll-behavior-block: contain;
		z-index: 1;
	`,
	scrollContainer: styled.div`
		padding-bottom: calc(${tokens.spacing.x0_25} + ${tokens.spacing.x2});
		opacity: 0;
		transition: opacity 100ms ease-in-out;
	`,
	newMessageBannerWrapper: styled.div`
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		display: flex;
		height: 60px;
		justify-content: center;
		align-items: center;
		z-index: 1;
	`,
	newMessageBanner: styled(motion.button)`
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		all: unset;
		display: flex;
		justify-content: center;
		background-color: ${tokens.color.bg.surface};
		padding: ${tokens.size.x1} ${tokens.size.x3};
		font: ${tokens.text.label.largeSemiBold};
		color: ${tokens.color.content.interactive};
		border-radius: ${tokens.borderRadius.pill};
		box-shadow: ${tokens.elevation.floating};
		border: 2px solid ${tokens.color.content.interactive};
		user-select: none;
		transition: box-shadow 150ms ease-in;
		overflow: hidden;
		position: relative;

		&::after {
			content: '';
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			transition: background-color 150ms ease-in;
			z-index: -1;
		}

		&:hover {
			&::after {
				background-color: ${tokens.color.bg.interactiveMutedHover};
			}
		}

		&:active {
			&::after {
				background-color: ${tokens.color.bg.interactiveMutedActive};
			}
		}
	`,
};

export function ChatAutoScroller({ children }: Props): JSX.Element {
	const [isBlockingNewMessageSpam, setIsBlockingNewMessageSpam] = useState(false);
	const isAutoScrolling = useRef(true);
	const shouldPreventProgrammaticScroll = useRef(false);
	const [hasHadAChildUpdate, setHasHadAChildUpdate] = useState(false);

	// ╔═ container & scroller ╗              The idea is that the "container" element
	// ║ ┌  scrollContainer  ┐ ║              retrieves its parent's dimensions so it
	// ║  ┌─── firstChild ──┐  ║              can be fitted anywhere.
	// ║ ││                 ││ ║
	// ║  │                 │  ║              To make the "scroller" element be
	// ║ ││                 ││ ║              scrollable within it's parent container
	// ║  │                 │  ║              - while having dynamic height - its
	// ║ ││                 ││ ║              using CSS absolute position. If it's not
	// ║  │                 │  ║              done this way, the scroller element will
	// ║ ││                 ││ ║              be needing a fixed height which is not
	// ╚══│                 │══╝              ideal.
	//   ││                 ││
	//    │                 │                 And to give the scroller's something to
	//   ││                 ││                scroll, a inner element is needed. This
	//    │                 │                 is the "scrollContainer".
	//   ││                 ││
	//    │                 │                 The last one, "firstChild", is only
	//   ││                 ││                there to provide something to make sure
	//    │                 │                 the "jump to" animation isn't
	//   │└─────────────────┘│                interfering with the scroll position.
	//    ─ ─ ─ ─ ─ ─ ─ ─ ─ ─
	//
	const scrollerRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// To prevent the chat to scroll to the bottom if the user has manually
	// scrolled the container.
	function handleScroll(_: UIEvent<HTMLDivElement>): void {
		const scrollerRefElement = scrollerRef.current;
		if (scrollerRefElement == null) return;

		const scroll = scrollerRefElement.scrollHeight - scrollerRefElement.scrollTop;
		const shouldAutoScroll = (scroll / scrollerRefElement.clientHeight) * 10 <= 12;
		const couldReceiveNewMessageSpam = (scroll / scrollerRefElement.clientHeight) * 10 <= 15;

		isAutoScrolling.current = shouldAutoScroll;
		setIsBlockingNewMessageSpam(shouldAutoScroll);

		if (couldReceiveNewMessageSpam) {
			setHasHadAChildUpdate(false);
		}
	}

	function handleManuallyScrollToBottomClick(): void {
		const maxHeight = scrollContainerRef.current?.clientHeight ?? Number.MAX_SAFE_INTEGER;

		scrollerRef.current?.scroll({
			behavior: 'smooth',
			top: maxHeight,
		});

		setHasHadAChildUpdate(false);
		setIsBlockingNewMessageSpam(true);
		isAutoScrolling.current = true;
	}

	useEffect(function toKeepRelativeScrollPositionWhenInputAreaChangesSize() {
		if (!__visiba_feature_flags__['PatientWeb.UseRelativeScrollPositionInChat']) return;

		if (containerRef.current == null) return;
		const containerElement = containerRef.current;

		let prevHeight = containerElement.clientHeight;

		const observer = new ResizeObserver((entries) => {
			if (scrollerRef.current == null) return;
			const scrollerRefElement = scrollerRef.current;

			const entry = entries[0];
			if (entry == null) return;

			const oldHeight = prevHeight;
			prevHeight = entry.contentRect.height;

			const top = scrollerRefElement.scrollTop + (oldHeight - prevHeight);

			scrollerRefElement.scroll({
				top,
			});
		});

		observer.observe(containerElement);

		return () => {
			observer.disconnect();
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function toHandleScrollWithinTheChat() {
			if (scrollContainerRef.current == null) return;
			const scrollContainerElement = scrollContainerRef.current;

			// ╔═┌┌─────────────────┐┐═╗          Once new messages is added, it'll
			// ║  │          ██████ │  ║          increase the height of the scroller
			// ║ ││ █████████       ││ ║          element, triggering the ResizeObserver's
			// ║  │        ████████ │  ║          callback.
			// ╚═││          ██████ ││═╝
			//    │ ████████        │
			//   ││ █████           ││
			//    │         █ new █ │
			//   └└─────────────────┘┘
			//
			const observer = new ResizeObserver(() => {
				if (scrollerRef?.current == null) return;
				const scrollerRefElement = scrollerRef.current;
				const childNodes = scrollContainerElement.firstChild?.childNodes;
				const childrenLength = scrollContainerElement.firstChild?.childNodes.length ?? 0;

				if (!isAutoScrolling.current && childrenLength > 0) {
					const lastElement = childNodes?.item(childNodes.length - 1);
					const wasMyMessage = (lastElement as HTMLDivElement | undefined)?.attributes.getNamedItem('data-is-me')?.value;
					setHasHadAChildUpdate(wasMyMessage === 'false');

					return;
				}

				let behavior: ScrollBehavior = 'smooth';
				//   ┌┌─────────────────┐┐        In normal circumstances when one or two
				//    │          ██████ │         messages is being received, it'll just
				//   ││ █████████       ││        set the scroll height to the bottom of
				//    │        ████████ │         the scroller element.
				// ╔═││          ██████ ││═╗
				// ║  │ ████████        │  ║
				// ║ ││ █████           ││ ║
				// ║  │         █ new █ │  ║
				// ╚═└└─────────────────┘┘═╝
				//
				let top = scrollContainerElement.clientHeight;

				//                                But we also want a nice lil animation
				//                                when the total messages exceeds a
				//                                certain limit to visually indicate that
				//                                there's more messages above. The
				//                                "jumpTo" variable is determining if that
				//
				const scrolledTo = scrollerRefElement.scrollTop ?? 0;
				const height = scrollerRefElement.clientHeight ?? 0;
				const expectedHeight = scrollerRefElement.scrollHeight ?? 0;
				const jumpTo = scrolledTo === 0 && expectedHeight > height;
				if (jumpTo) {
					behavior = 'auto';
					if (scrollContainerRef?.current == null) return;
					const scrollContainer = scrollContainerRef.current;
					const animationElement = scrollContainer.firstChild as HTMLElement | null;
					if (animationElement == null) return;

					// ╔═┌┌─────────────────┐┐═╗    If the "new message banner" exists in
					// ║  │          ███████│  ║    the DOM, we don't want to animate to the
					// ║ ││ ███████         ││ ║    bottom of the "scroller" element. But
					// ║  │ █████           │  ║    rather scroll the "new message banner"
					// ╚═││ ██████          ││═╝    into view.
					//    │          ███████│
					//   ││                 ││
					//    │ ───── new ───── │
					//   ││                 ││
					//    │ ███████         │
					//   ││ █████           ││
					//    │ ████████        │
					//   └└─────────────────┘┘
					//
					const newMessagesBanner = document.getElementById(NEW_MESSAGES_BANNER_ID);
					if (newMessagesBanner != null) {
						//   ┌┌─────────────────┐┐    Like so.
						//    │          ███████│
						//   ││ ███████         ││
						//    │ █████           │
						//   ││ ██████          ││
						//    │          ███████│
						// ╔═││                 ││═╗
						// ║  │ ───── new ───── │  ║
						// ║ ││                 ││ ║
						// ║  │ ███████         │  ║
						// ╚═││ █████           ││═╝
						//    │ ████████        │
						//   └└─────────────────┘┘
						//
						top = newMessagesBanner.offsetTop - 14; // -14 is arbitrary. Makes its scroll position a bit nicer.
					}

					//   ┌┌─────────────────┐┐      The remaining code below is just to
					//    │          ███████│       create the animation for scrolling down
					//   ││ ███████         ││      the the latest messages. 🚀
					//    │ █████           │
					//   ││ ██████          ││
					//    │          ███████│
					//   ││            █████││
					//    │         ████████│
					// ╔═││            █████││═╗
					// ║  │ ███████         │  ║
					// ║ ││ █████           ││ ║
					// ║  │ ████████        │  ║
					// ╚═└└─────────────────┘┘═╝
					//
					function getAutoHeightDuration(height: number): number {
						const constant = height / 2;

						return Math.round((4 + (15 * constant ** 0.25 + constant) / 5) * 10);
					}

					const height = clamp(childrenLength * 10, [0, 600]);

					animationElement.style.transform = `translateY(${height}px)`;

					requestAnimationFrame(() => {
						scrollContainer.style.opacity = '1';
						animationElement.style.transform = 'translateY(0px)';
						animationElement.style.transition = `transform ${getAutoHeightDuration(height)}ms cubic-bezier(0.18, 0.89, 0.32, 1.05) 0s`;
					});
				} else {
					if (scrollContainerRef?.current == null) return;
					const scrollContainer = scrollContainerRef.current;
					scrollContainer.style.transition = 'opacity 250ms ease-in-out';
					scrollContainer.style.opacity = '1';
				}

				if (!shouldPreventProgrammaticScroll.current) {
					scrollerRefElement?.scroll({
						behavior,
						top,
					});
				}
			});

			observer.observe(scrollContainerElement);

			return () => {
				observer.unobserve(scrollContainerElement);
			};
		},
		[scrollContainerRef],
	);

	function handleTouchStart(): void {
		shouldPreventProgrammaticScroll.current = true;
	}

	function handleTouchEnd(): void {
		shouldPreventProgrammaticScroll.current = false;
	}

	return (
		<elements.container ref={containerRef}>
			<elements.scroller ref={scrollerRef} onScroll={handleScroll} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
				<elements.scrollContainer style={{ overflow: 'hidden' }} ref={scrollContainerRef}>
					{children}
				</elements.scrollContainer>
			</elements.scroller>

			<NewMessageFloatingButton show={!isBlockingNewMessageSpam && hasHadAChildUpdate} onClick={handleManuallyScrollToBottomClick} />
		</elements.container>
	);
}
